import debounce from 'lodash/debounce'

import {getBuildType, getIsProjectOnOverview, getOwnerProjectId, getProject} from '../selectors'
import {restApi} from '../services/rest'
import type {ProjectOrBuildTypeNode} from '../types'
import {stringifyId} from '../types'

import type {AppThunk} from './types'

const invalidateTagsWaitTime = 700
const invalidateTagsDebounce = debounce(
  dispatch => dispatch(restApi.util.invalidateTags(['Overview'])),
  invalidateTagsWaitTime,
  {leading: false, trailing: true},
)

export const toggleOverview =
  (node: ProjectOrBuildTypeNode, on: boolean, skipSubprojects?: boolean): AppThunk =>
  async (dispatch, getState) => {
    if (!node.id) {
      return
    }

    const state = getState()
    const projectInternalId =
      node.nodeType === 'project' ? getProject(state, node.id)?.internalId : null
    const buildTypeInternalId =
      node.nodeType === 'project' ? null : getBuildType(state, node.id)?.internalId

    if (!projectInternalId && !buildTypeInternalId) {
      throw new Error(
        `${node.nodeType === 'project' ? 'Project' : 'Build Configuration'} with id ${stringifyId(
          node.id,
        )} not found in store, or internalId field is missing`,
      )
    }

    if (node.nodeType === 'project') {
      await dispatch(
        restApi.endpoints.toggleFavoriteProject.initiate({
          projectId: node.id,
          projectInternalId: projectInternalId!,
          on,
          skipSubprojects,
        }),
      )
    } else {
      const ownerProjectId = getOwnerProjectId(state, node.id)

      if (on && ownerProjectId && !getIsProjectOnOverview(state, ownerProjectId)) {
        const ownerProjectInternalId = getProject(state, ownerProjectId)?.internalId
        if (!ownerProjectInternalId) {
          throw new Error(
            `Project with id ${stringifyId(
              node.id,
            )} not found in store, or internalId field is missing`,
          )
        }
        await dispatch(
          restApi.endpoints.toggleFavoriteProject.initiate({
            projectId: ownerProjectId,
            projectInternalId: ownerProjectInternalId,
            on: true,
            skipSubprojects: true,
          }),
        )
        await dispatch(
          restApi.endpoints.setFavoriteBuildTypes.initiate({
            projectId: ownerProjectId,
            buildTypeIds: [node.id],
            buildTypeInternalIds: [buildTypeInternalId!],
          }),
        )
      } else if (ownerProjectId) {
        await dispatch(
          restApi.endpoints.toggleFavoriteBuildType.initiate({
            projectId: ownerProjectId,
            buildTypeId: node.id,
            buildTypeInternalId: buildTypeInternalId!,
            on,
          }),
        )
      }
    }

    invalidateTagsDebounce(dispatch)
  }
